import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { AnalyticsCommonTypes } from '@frontend/api-analytics';
import { AI_OFFICE_USER_UUID } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { AiOfficeUser } from '@frontend/phone-ui';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { useCallIntelFiltersContext } from '../../context/call-intel-filters.context';

export interface UseOfficeUserFilterDataProps {
  officeUsers?: AnalyticsCommonTypes.StringRecord;
  showAiReceptionist?: boolean;
}

export const useOfficeUserFilterData = ({
  officeUsers = {},
  showAiReceptionist = false,
}: UseOfficeUserFilterDataProps = {}) => {
  const { t } = useTranslation('call-intelligence');

  const { filterValues, updateFilterValues, updateFilterModified, isLoading } = useCallIntelFiltersContext();

  const groupedOfficeUsers = useMemo(() => {
    if (!showAiReceptionist) {
      return [
        {
          children: Object.entries(officeUsers).map(([id, label]) => ({
            label,
            value: id,
          })),
          label: t('Office Users'),
          value: 'office-users-group',
          hideLabel: true,
        },
      ];
    }

    return [
      {
        children: [],
        label: (
          <div css={styles.aiReceptionistLabel}>
            <AiOfficeUser />
          </div>
        ),
        labelText: t('AI Receptionist'),
        value: AI_OFFICE_USER_UUID,
      },
      {
        children: Object.entries(officeUsers).map(([id, label]) => ({
          label,
          value: id,
        })),
        label: <Text weight='bold'>{t('Office Staff')}</Text>,
        value: 'office-users-group',
      },
    ];
  }, [officeUsers, showAiReceptionist, t]);

  const hasOptions = useMemo(() => Object.keys(officeUsers).length > 0, [officeUsers]);

  // Cleanup selected office users when available users change (e.g., location changes)
  useEffect(() => {
    const selectedUsers = filterValues.officeUsers || [];

    if (selectedUsers.length === 0) {
      return;
    }

    const availableUserIds = new Set(Object.keys(officeUsers));
    if (showAiReceptionist) {
      availableUserIds.add(AI_OFFICE_USER_UUID);
    }

    const validSelectedUsers = selectedUsers.filter((userId) => availableUserIds.has(userId));

    if (validSelectedUsers.length !== selectedUsers.length) {
      updateFilterValues({ officeUsers: validSelectedUsers });
    }
  }, [officeUsers, showAiReceptionist, filterValues.officeUsers, updateFilterValues]);

  useEffect(() => {
    updateFilterModified({ officeUsers: !!filterValues.officeUsers?.length });
  }, [filterValues.officeUsers, updateFilterModified]);

  const onChange = useCallback(
    (value: string[]) => {
      updateFilterValues({ officeUsers: value });
    },
    [updateFilterValues]
  );

  return {
    groupedOfficeUsers,
    value: filterValues.officeUsers || [],
    defaultValue: [] as string[],
    hasOptions,
    isLoading,
    onChange,
  };
};

const styles = {
  aiReceptionistLabel: css`
    width: max-content;
    p {
      font-size: ${theme.font.size.large};
      font-weight: ${theme.font.weight.bold};
    }
  `,
};
